import { useCallback, useMemo } from "react";
import useActiveOrganization from "./useActiveOrganization";

/**
 * useHasFeature(subscriptionOverride?)
 *
 * - If `subscriptionOverride` is provided, use that.
 * - Otherwise, use the active organization's subscription from useActiveOrganization().
 */
export default function useHasFeature(subscriptionOverride) {
    const { activeSubscription } = useActiveOrganization();

    // Decide which subscription to use
    const subscription = subscriptionOverride ?? activeSubscription;

    const featureKeys = useMemo(() => {
        const features = subscription?.plan?.features;
        if (!Array.isArray(features)) return new Set();
        return new Set(
            features
                .map((f) => f?.key)
                .filter(Boolean)
        );
    }, [subscription]);

    const hasFeature = useCallback(
        (featureKey) => {
            if (!featureKey) return false;
            return featureKeys.has(featureKey);
        },
        [featureKeys]
    );

    const hasAnyFeature = useCallback(
        (keys = []) => keys.some((k) => featureKeys.has(k)),
        [featureKeys]
    );

    return {
        subscription,
        hasFeature,
        hasAnyFeature,
    };
}